// src/hooks/useLocale.ts
// Hook para manejar el idioma y preferencias de localización

import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  getLocaleConfig,
  isSupportedLocale,
  type LocaleConfig,
  type SupportedLocaleCode,
} from "../convex/lib/config";

// ============================================================================
// TYPES
// ============================================================================

/**
 * Opciones de formato de fecha
 */
export interface DateFormatOptions {
  format?: "short" | "long" | "withTime" | "time";
  locale?: SupportedLocaleCode;
}

/**
 * Opciones de formato de número
 */
export interface NumberFormatOptions {
  style?: "decimal" | "currency" | "percent";
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  locale?: SupportedLocaleCode;
}

/**
 * Resultado del hook useLocale
 */
export interface UseLocaleResult {
  /** Código del idioma actual */
  locale: SupportedLocaleCode;
  /** Configuración completa del locale actual */
  localeConfig: LocaleConfig;
  /** Lista de locales disponibles */
  availableLocales: readonly LocaleConfig[];
  /** Si el idioma está cargado y listo */
  ready: boolean;
  
  /** Cambia el idioma */
  changeLocale: (locale: SupportedLocaleCode) => Promise<void>;
  /** Verifica si un código de locale es válido */
  isValidLocale: (code: string) => boolean;
  
  /** Formatea una fecha según el locale */
  formatDate: (date: Date | number | string, options?: DateFormatOptions) => string;
  /** Formatea un número según el locale */
  formatNumber: (value: number, options?: NumberFormatOptions) => string;
  /** Formatea moneda según el locale */
  formatCurrency: (value: number, currency?: string) => string;
  /** Formatea porcentaje según el locale */
  formatPercent: (value: number, decimals?: number) => string;
  /** Obtiene tiempo relativo (hace 5 minutos, etc.) */
  formatRelativeTime: (date: Date | number) => string;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Convierte un valor a Date
 */
function toDate(value: Date | number | string): Date {
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  return new Date(value);
}

/**
 * Obtiene el Intl locale string para un código
 */
function getIntlLocale(code: SupportedLocaleCode): string {
  const localeMap: Record<SupportedLocaleCode, string> = {
    es: "es-HN",
    en: "en-US",
  };
  return localeMap[code] ?? code;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook principal para manejar localización
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { 
 *     locale, 
 *     changeLocale, 
 *     formatDate, 
 *     formatCurrency 
 *   } = useLocale();
 *   
 *   return (
 *     <div>
 *       <select 
 *         value={locale} 
 *         onChange={(e) => changeLocale(e.target.value as SupportedLocaleCode)}
 *       >
 *         {availableLocales.map(l => (
 *           <option key={l.code} value={l.code}>
 *             {l.flag} {l.nativeName}
 *           </option>
 *         ))}
 *       </select>
 *       
 *       <p>Fecha: {formatDate(new Date())}</p>
 *       <p>Precio: {formatCurrency(1500.50)}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useLocale(): UseLocaleResult {
  const { i18n, ready } = useTranslation();
  
  // Locale actual
  const locale = useMemo(
    () => (isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE),
    [i18n.language]
  );
  
  // Configuración del locale actual
  const localeConfig = useMemo(
    () => getLocaleConfig(locale) ?? getLocaleConfig(DEFAULT_LOCALE)!,
    [locale]
  );
  
  // Cambiar idioma
  const changeLocale = useCallback(
    async (newLocale: SupportedLocaleCode): Promise<void> => {
      if (!isSupportedLocale(newLocale)) {
        console.warn(`Locale "${newLocale}" is not supported`);
        return;
      }
      
      await i18n.changeLanguage(newLocale);
      
      // Guardar en localStorage
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("i18nextLng", newLocale);
      }
      
      // Actualizar el atributo lang del documento
      if (typeof document !== "undefined") {
        document.documentElement.lang = newLocale;
        document.documentElement.dir = getLocaleConfig(newLocale)?.direction ?? "ltr";
      }
    },
    [i18n]
  );
  
  // Formatear fecha
  const formatDate = useCallback(
    (date: Date | number | string, options: DateFormatOptions = {}): string => {
      const { format = "short", locale: overrideLocale } = options;
      const targetLocale = overrideLocale ?? locale;
      const config = getLocaleConfig(targetLocale) ?? localeConfig;
      const dateObj = toDate(date);
      
      const intlLocale = getIntlLocale(targetLocale);
      
      try {
        switch (format) {
          case "long":
            return new Intl.DateTimeFormat(intlLocale, {
              year: "numeric",
              month: "long",
              day: "numeric",
            }).format(dateObj);
            
          case "withTime":
            return new Intl.DateTimeFormat(intlLocale, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            }).format(dateObj);
            
          case "time":
            return new Intl.DateTimeFormat(intlLocale, {
              hour: "2-digit",
              minute: "2-digit",
            }).format(dateObj);
            
          case "short":
          default:
            return new Intl.DateTimeFormat(intlLocale, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }).format(dateObj);
        }
      } catch {
        return dateObj.toLocaleDateString();
      }
    },
    [locale, localeConfig]
  );
  
  // Formatear número
  const formatNumber = useCallback(
    (value: number, options: NumberFormatOptions = {}): string => {
      const {
        style = "decimal",
        currency,
        minimumFractionDigits,
        maximumFractionDigits,
        locale: overrideLocale,
      } = options;
      
      const targetLocale = overrideLocale ?? locale;
      const intlLocale = getIntlLocale(targetLocale);
      
      try {
        const formatter = new Intl.NumberFormat(intlLocale, {
          style,
          currency: style === "currency" ? currency ?? localeConfig.defaultCurrency : undefined,
          minimumFractionDigits,
          maximumFractionDigits,
        });
        
        return formatter.format(value);
      } catch {
        return value.toString();
      }
    },
    [locale, localeConfig]
  );
  
  // Formatear moneda
  const formatCurrency = useCallback(
    (value: number, currency?: string): string => {
      return formatNumber(value, {
        style: "currency",
        currency: currency ?? localeConfig.defaultCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    [formatNumber, localeConfig]
  );
  
  // Formatear porcentaje
  const formatPercent = useCallback(
    (value: number, decimals = 0): string => {
      return formatNumber(value / 100, {
        style: "percent",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
    [formatNumber]
  );
  
  // Formatear tiempo relativo
  const formatRelativeTime = useCallback(
    (date: Date | number): string => {
      const dateObj = toDate(date);
      const now = new Date();
      const diffMs = now.getTime() - dateObj.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffDays / 365);
      
      const intlLocale = getIntlLocale(locale);
      
      try {
        const rtf = new Intl.RelativeTimeFormat(intlLocale, { numeric: "auto" });
        
        if (diffSecs < 60) {
          return rtf.format(-diffSecs, "second");
        } else if (diffMins < 60) {
          return rtf.format(-diffMins, "minute");
        } else if (diffHours < 24) {
          return rtf.format(-diffHours, "hour");
        } else if (diffDays < 7) {
          return rtf.format(-diffDays, "day");
        } else if (diffWeeks < 4) {
          return rtf.format(-diffWeeks, "week");
        } else if (diffMonths < 12) {
          return rtf.format(-diffMonths, "month");
        } else {
          return rtf.format(-diffYears, "year");
        }
      } catch {
        // Fallback simple
        if (diffMins < 1) return locale === "es" ? "ahora mismo" : "just now";
        if (diffMins < 60) return locale === "es" ? `hace ${diffMins} min` : `${diffMins} min ago`;
        if (diffHours < 24) return locale === "es" ? `hace ${diffHours}h` : `${diffHours}h ago`;
        return formatDate(dateObj);
      }
    },
    [locale, formatDate]
  );
  
  return {
    locale,
    localeConfig,
    availableLocales: SUPPORTED_LOCALES,
    ready,
    changeLocale,
    isValidLocale: isSupportedLocale,
    formatDate,
    formatNumber,
    formatCurrency,
    formatPercent,
    formatRelativeTime,
  };
}

/**
 * Hook simplificado solo para formateo de fechas
 * 
 * @example
 * ```tsx
 * function DateDisplay({ date }: { date: Date }) {
 *   const { format, formatRelative } = useDateFormat();
 *   return <span title={format(date, "long")}>{formatRelative(date)}</span>;
 * }
 * ```
 */
export function useDateFormat() {
  const { formatDate, formatRelativeTime, locale } = useLocale();
  
  return {
    format: formatDate,
    formatRelative: formatRelativeTime,
    locale,
  };
}

/**
 * Hook simplificado solo para formateo de números/moneda
 * 
 * @example
 * ```tsx
 * function PriceDisplay({ amount }: { amount: number }) {
 *   const { currency, number } = useNumberFormat();
 *   return <span>{currency(amount)}</span>;
 * }
 * ```
 */
export function useNumberFormat() {
  const { formatNumber, formatCurrency, formatPercent, locale, localeConfig } = useLocale();
  
  return {
    number: formatNumber,
    currency: formatCurrency,
    percent: formatPercent,
    locale,
    defaultCurrency: localeConfig.defaultCurrency,
  };
}
